import React, { useContext, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { LoadingContext } from '../../context/LoadingContext';
import useUnitDashboardData from '../../hooks/useUnitDashboardData';
import CardV2 from '../CardV2';
import CardSkeleton from '../common/CardSkeleton';
import BatteryCard from '../cards/BatteryCard';
import getCardConfigs, { mobileOrder, desktopOrder } from '../../utils/cardConfigs';

/**
 * DashboardNavigationSW25 Component
 *
 * This component renders the dashboard view for a selected unit, displaying usage, location,
 * and water change data. It uses the useUnitDashboardData hook to fetch raw data and error states,
 * and passes these to the getCardConfigs function, which returns formatted configurations for each card.
 * Depending on the loading state from the global LoadingContext, it either
 * displays skeleton loaders or renders the CardV2 components. Any error feedback is handled automatically
 * via the global FeedbackContext (e.g., toast notifications), invoked inside the hook.
 *
 * Props:
 * @param {Object} selectedUnit - The unit object currently selected, used to fetch the relevant data.
 *
 * The component is memoized to prevent unnecessary re-renders when the selected unit remains unchanged.
 */

const CardFactory = React.memo(({ cardConfig }) => {
  const componentsById = { battery: BatteryCard };
  const Component = componentsById[cardConfig.id] || CardV2;
  return <Component {...cardConfig} />;
});

const DashboardNavigationSW25 = ({ selectedUnit }) => {
  const isMobile = useMediaQuery({ query: '(max-width: 1023px)' });
  const { isLoading } = useContext(LoadingContext);

  const {
    usageData,
    locationData,
    waterChangeData,
    batteryLifeData,
    errors,
    requestTimestamp,
    cardErrors,
  } = useUnitDashboardData(selectedUnit);

  const cardConfigs = useMemo(
    () =>
      getCardConfigs({
        usageData,
        locationData,
        waterChangeData,
        batteryLifeData,
        errors,
        requestTimestamp,
        cardErrors,
      }),
    [
      usageData,
      locationData,
      waterChangeData,
      batteryLifeData,
      errors,
      requestTimestamp,
      cardErrors,
    ],
  );

  const skeletonCount = 2;

  const sortedConfigs = useMemo(() => {
    const order = isMobile ? mobileOrder : desktopOrder;
    return [...cardConfigs].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [cardConfigs, isMobile]);

  // Build an array of placeholders or real items
  const items = useMemo(
    () =>
      isLoading
        ? Array.from({ length: skeletonCount }, (_, i) => ({
            id: `skeleton-${i}`,
            isSkeleton: true,
          }))
        : sortedConfigs,
    [isLoading, sortedConfigs],
  );

  return (
    <div className="bg-custom-light-gray text-custom-dark-blue mt-12 w-full">
      <div className="flex flex-wrap justify-center gap-8 ">
        {items.map((card) =>
          card.isSkeleton ? (
            <CardSkeleton key={card.id} />
          ) : (
            <CardFactory key={card.id} cardConfig={card} />
          ),
        )}
      </div>
    </div>
  );
};

export default React.memo(
  DashboardNavigationSW25,
  (prevProps, nextProps) => prevProps.selectedUnit?.id === nextProps.selectedUnit?.id,
);
